import 'leaflet/dist/leaflet.css';

import * as Leaflet from 'leaflet';
import { INode } from '../../helpers/interfaces';
import { ImageOverlay, MapContainer, Marker, Polygon, Popup, useMap, useMapEvent } from 'react-leaflet';
import React, { Component, useEffect } from 'react';
import { heatmapColors, polygonColors } from '../HeatMapInstaller/constants';
import { ENDPOINT_DEVICE } from '../../helpers/Devices';
import ErrorComponent from '../ErrorComponent/ErrorComponent';
import HeatmapLayer from '../react-leaflet-heatmap-layer/HeatmapLayer.js';
import { NavLink } from 'react-router-dom';
import { devicesIcons } from '../HeatMapInstaller/devicesIcons';
import { SENSORS } from '../../helpers/Devices';
import Loading from '../Loading/Loading';
import ErrorText from '../ErrorComponent/ErrorText';
import Button from '../../../components/CustomButtons/ButtonTS';
import PopupAutoPanFix from './PopupAutoPanFix.js';
import MapInitializer from './MapInitializer';
import pixelToLatLng from './PixelToLatLng';
import ArrowBack from '@material-ui/icons/ArrowLeft';
import ArrowForward from '@material-ui/icons/ArrowRight';
import { connect } from 'react-redux';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';

const infrButtonStyle = {
	background: "linear-gradient(60deg, #7df200, #31b800)",
	color: "#fff",
	padding: "20px",
	marginTop: '25px',
	marginLeft: '15px',
	transition: 'color 0.3s',
	fontWeight: '500'
};

interface HeatMapProps {
	mapHeight: number;
	mapWidth: number;
	mapBoundaries: number[][];
	mainMapDataBlob: string;
	nodes: INode[];
	mobileView: boolean;
}

interface HeatMapState {
	wHei: number;
	wWidth: number;
	map: Leaflet.Map | null;
	bounds: Leaflet.LatLngBounds | null;
	zoomArg: number | null;
	scaleFactor: number | null;
	zooming: boolean;
	scrollWheelZoom: boolean;
	isMapLoaded: boolean;
	firstRender: boolean;
	fullscreenClicked: boolean;
	showAllDevices: boolean;
	currentMap: string;
}

class HeatMap extends Component<HeatMapProps, HeatMapState> {
	constructor(props) {
		super(props);
		this.state = {
			wHei: 0,
			wWidth: 0,
			map: null,
			bounds: null,
			zoomArg: null,
			scaleFactor: null,
			zooming: false,
			scrollWheelZoom: false,
			isMapLoaded: false,
			firstRender: true,
			fullscreenClicked: false,
			showAllDevices: false,
			currentMap: "main"
		};
	}

	componentDidCatch(error, errorInfo) {
		// TODO: stworzyć globalny łapacz błędów albo użyc sentry
		console.error('ERROR__', error, errorInfo);
	}

	heatmapPoints() {
		const points = [];
		if (!this.props.nodes) {
		    return points;
		}
		this.props.nodes.forEach((node) => {
			let power = 0;
			if (node.LatestKnownThreatLevel !== 'none' && node.LatestKnownThreatLevel !== 'unknown_or_NA' && node.active) {
				power = parseInt(node.LatestKnownThreatLevel.split('L')[1], 10) * 25;
				points.push([node.mapPositionX, node.mapPositionY, power]);
			}
		});
		return points;
	}

	renderPopup = (node: INode, i: number) => {
		const name = node.name;
		const isInclinometer = node.MainSensorType === 'inclinometer';
		let obstruction = '';
		let deflection = '';
		let threshold = '';

		if (node.CanMeasure && (node.SystemVersion !== 'W2' || node.W2Operational === true)) {
			// calculate deflection value:
			if (node.PermissibleValue !== null && node.LatestKnownCalculatedDeflection !== null) {
				deflection = `Deflection: 0%`;
				if (node.PermissibleValue !== 0) {
					const deflectionPercent = `${Math.round(node.LatestKnownCalculatedDeflection * 100 / node.PermissibleValue)}%`;
					const deflectionValue = node.LatestKnownCalculatedDeflection.toFixed(isInclinometer ? 3 : 0);
					const unit = isInclinometer ? '°' : 'mm';
					deflection = `Deflection: ${deflectionPercent} (${deflectionValue}${unit})`;
				}
			} else {
				deflection = `Deflection: --`;
			}

			// calculate obstructing value:
			let baseDistanceCorrectionValue;
			if (node.BaseDistanceCorrection !== null && node.ObstructionStatus) {
				if (Math.abs(node.BaseDistanceCorrection) < 99) {
					baseDistanceCorrectionValue = `: ca. ${(Math.abs(node.BaseDistanceCorrection) / 10).toFixed()}cm`;
				} else if (Math.abs(node.BaseDistanceCorrection) < 999) {
					baseDistanceCorrectionValue = `: ca. ${(Math.abs(node.BaseDistanceCorrection) / 10).toFixed()}cm`
				} else if (Math.abs(node.BaseDistanceCorrection) < 9999) {
					baseDistanceCorrectionValue = `: ca. ${(Math.abs(node.BaseDistanceCorrection) / 1000).toFixed(1)}m`
				} else {
					baseDistanceCorrectionValue = `: ca. ${(Math.abs(node.BaseDistanceCorrection) / 1000).toFixed()}m`
				}
				obstruction = `Node obstructed${node.BaseDistanceCorrection !== 0 ? baseDistanceCorrectionValue : ""}`;
			}
		}

		if (node.LatestKnownThreatLevel !== 'none' && node.LatestKnownThreatLevel !== 'unknown_or_NA' && node.active) {
			threshold = `Threshold: ${node.LatestKnownThreatLevel}`;
		}

		return (
			<Popup key={`popup-${i}`} autoPan={true}>
				<NavLink to={`/devices/${node.id}/graph`} key={`node-name-${name}`}>
					<div>{name}</div>
					<div>{deflection}</div>
					<div>{obstruction}</div>
					<div>{threshold}</div>
				</NavLink>
			</Popup>
		);
	}

	handleShowNextMap = () => {
		const keys = ["main", "second", "third"].filter(
			key => this.props[`${key}MapDataBlob`]
		);
		const currentIndex = keys.indexOf(this.state.currentMap);
		const nextIndex = (currentIndex + 1) % keys.length;
		this.setState({ currentMap: keys[nextIndex] });
	};

	handleShowPrevMap = () => {
		const keys = ["main", "second", "third"].filter(
			key => this.props[`${key}MapDataBlob`]
		);
		const currentIndex = keys.indexOf(this.state.currentMap);
		const prevIndex = (currentIndex - 1 + keys.length) % keys.length;
		this.setState({ currentMap: keys[prevIndex] });
	};

	getCurrentMapUrl = () => {
		switch (this.state.currentMap) {
			case "main":
				return this.props.mainMapDataBlob;
			case "second":
				return this.props.secondMapDataBlob;
			case "third":
				return this.props.thirdMapDataBlob;
			default:
				return this.props.mainMapDataBlob;
		}
	};


	render() {
		try {
			const state = {
				zoom: 1,
				minZoom: 0,
				maxZoom: 5,
				zoomSnap: 0.1,
				zoomDelta: 0.1,
				wheelPxPerZoomLevel: 120,
				infinite: false,
				center: [0.0, 0.0],
				noWrap: true,
				boundaryOutline: this.props.mapBoundaries,
				zoomProjectionMargin: 1,
				heatmap: this.heatmapPoints(),
			};

			const { mapWidth, mapHeight } = this.props;
			const crsSimple = Leaflet.CRS.Simple;
			const map = this.state.map;
			const bounds = this.state.bounds;

			const wHei = window.innerHeight * 0.55;

			let boundaryOutlineUnprojected: any[] = [];
			let heatmapUnprojected: any[] = [];
			let markers: any = [];
			let maxThreatLvl = 0;

			if (map && bounds) {
				markers = !this.props.nodes ? [] : this.props.nodes.map((node, i) => {
					if (!this.state.showAllDevices && (node.DeviceRole !== ENDPOINT_DEVICE && !node.CanMeasure || (node.SystemVersion === 'W2' && !node.W2Operational))) {
						return <></>
					}
					let content;

					let threatLvl = 0;
					if (node.LatestKnownThreatLevel === 'unknown_or_NA' || !node.active) {
						threatLvl = 0;
					} else if (node.LatestKnownThreatLevel === 'none') {
						threatLvl = 1;
					} else {
						threatLvl = parseInt(node.LatestKnownThreatLevel.split('L')[1], 10) + 1;
					}

					// set heatmap color
					if (threatLvl > maxThreatLvl) {
						maxThreatLvl = threatLvl;
					}
					//if device is an endpoint device:
					if (node.MainSensorType === SENSORS.Laser.valueOf()) {
						content = devicesIcons.laser[threatLvl];
						//if device is obstructed:
						if (!!node.ObstructionStatus) {
							content = devicesIcons.laserObstructed[threatLvl];
						}
						//if device isn't active:
						if (node.active === false) {
							content = devicesIcons.laserNotActive;
						}
						//if W2 device isn't operational:
						if (node.SystemVersion === 'W2' && node.W2Operational === false) {
							content = devicesIcons.laserNotOperational;
						}
					}
					//if device is an inclinometer:
					else if (node.MainSensorType === SENSORS.Iclinometer.valueOf()) {
						content = devicesIcons.inclinometer[threatLvl];
						//if device is obstructed:
						if (!!node.ObstructionStatus) {
							content = devicesIcons.inclinometerObstructed[threatLvl];
						}
						//if device isn't active:
						if (node.active === false) {
							content = devicesIcons.inclinometerNotActive;
						}
						//if W2 device isn't operational:
						if (node.SystemVersion === 'W2' && node.W2Operational === false) {
							content = devicesIcons.inclinometerNotOperational;
						}
					}
					//if device is an access device or retransmitter:
					else if (node.name !== 'Gateway') {
						content = devicesIcons.infrastructureDeviceActiveOperational;

						//if device isn't active:
						if (node.active === false) {
							content = devicesIcons.infrastructureDeviceNotActive;
						}
						//if W2 device isn't operational:
						if (node.SystemVersion === 'W2' && node.W2Operational === false) {
							content = devicesIcons.infrastructureDeviceNotOperational;
						}
					//if device is a gateway:
					} else {
						content = devicesIcons.infrastructureDeviceActiveOperational;
					}

					let n = pixelToLatLng(node.mapPositionX, node.mapPositionY, bounds, mapWidth, mapHeight);

					return (
						<Marker
							icon={content}
							key={'wisene-sni-' + i}
							position={[n.lat, n.lng]}
							center={[n.lat, n.lng]}
						>
							{this.renderPopup(node, i)}
						</Marker>
					);
				});

				//drawing polygons from database:
				state.boundaryOutline.forEach((polygon) => {
					const currentPolygon = [];
					polygon.forEach((vertex, i) => {
						let c = pixelToLatLng(vertex[0], vertex[1], bounds, mapWidth, mapHeight);
						currentPolygon.push([c.lat, c.lng]);
					});
					boundaryOutlineUnprojected.push(currentPolygon);
				});

				//drawing threshold color:
				state.heatmap.forEach((p) => {
					let c =  pixelToLatLng(p[0], p[1], bounds, mapWidth, mapHeight);
					heatmapUnprojected.push([c.lat, c.lng, p[2]]);
				});
			}

			return (
				<ErrorComponent message='Corrupted map data.' Children>
					<div>
						<Button
							style={infrButtonStyle}
							onClick={()=>this.setState({showAllDevices: !this.state.showAllDevices})}
						>
                          { this.state.showAllDevices ?
                            'Show operational measuring devices' :
                            'Show all devices' }
						</Button>
					</div>
					{!this.props.mainMapDataBlob ? (
						<div
							style={{
								height: wHei,
								margin: '15px 15px 0 15px',
								borderRadius: '5px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center'
							}}
						>
							<Loading transform={0.85} height={90} width={70} color="gray" />
						</div>
					) : (
						<>
						<MapContainer
							data-testid="heatMap"
							crs={crsSimple}
							key={'wisenemap-heat'}
							{...((bounds && { maxBounds: bounds }) as any)}
							center={state.center}
							style={{
								width: '100% - 15px',
								height: wHei,
								margin: '15px 15px 0 15px',
								borderRadius: '5px'
							}}
							zoom={this.state.fullscreenClicked ? 1.25 : state.zoom}
							minZoom={state.minZoom}
							maxZoom={state.maxZoom}
							zoomSnap={state.zoomSnap}
							zoomDelta={state.zoomDelta}
							wheelPxPerZoomLevel={state.wheelPxPerZoomLevel}
							infinite={state.infinite as any}
							scrollWheelZoom={this.state.scrollWheelZoom}
							zoomAnimation={false as any}
							dragging={true as any}
							tap={false as any}
							// onClick={() => {
							// 	this.setState({ scrollWheelZoom: !this.state.scrollWheelZoom });
							// }}
							// onBlur={() => {
							// 	this.setState({ scrollWheelZoom: false });
							// }}
						>
							<MapInitializer
							mapWidth={mapWidth}
							mapHeight={mapHeight}
							zoomProjectionMargin={state.zoomProjectionMargin}
							onMapReady={(m, computedBounds, zoomArg) => {
								if (this.state.map !== m || this.state.bounds !== computedBounds || this.state.zoomArg !== zoomArg) {
								this.setState({ map: m, bounds: computedBounds, zoomArg });
								}
							}}
							/>
							<MapEvents
								onMapClick={() =>
									this.setState({ scrollWheelZoom: !this.state.scrollWheelZoom }, () => {
										this.state.scrollWheelZoom ? this.state.map.scrollWheelZoom.disable() : this.state.map.scrollWheelZoom.enable();
									})
								}
								onMapBlur={() =>
									this.state.map.scrollWheelZoom.disable()
								}
							/>
							 <PopupAutoPanFix />
							{map && bounds && <ImageOverlay bounds={bounds} url={this.getCurrentMapUrl()} />}

							{markers}
							{boundaryOutlineUnprojected.map((polygonContour, i) => {
								return (
									<React.Fragment key={`heat-map-fragment-${i}`}>
										<HeatmapLayer
											minOpacity={0.15}
											fitBoundsOn
											useFlyTo
											points={heatmapUnprojected}
											pointsHeatmapBoundry={polygonContour}
											radius={150}
											max={(maxThreatLvl - 1) * 25}
											blur={40}
											gradient={{ '1': heatmapColors[maxThreatLvl] }}
											longitudeExtractor={(m) => m[1]}
											latitudeExtractor={(m) => m[0]}
											intensityExtractor={(m) => parseFloat(m[2])}
											key={`heatmap-layer-${i}`}
										/>
										<Polygon
											color={polygonColors[maxThreatLvl]}
											fill={false}
											positions={polygonContour}
											zIndex={200}
											key={`polygon-contour-${i}`}
										/>
									</React.Fragment>
								);
							})}
							<Polygon key="default-poly" color="black" fill={false} positions={boundaryOutlineUnprojected} zIndex={201} />
						</MapContainer>
						{(!!this.props.secondMapDataBlob || !!this.props.thirdMapDataBlob) && <>
						<GridContainer>
							<GridItem xs={12} style={{ display: 'flex', alignItems: 'center', margin: '10px 15px 0 0' }}>
								<div style={{ display: 'flex', flex: 1, justifyContent: 'center', gap: '8px' }}>
								<Button size='lg' simple justIcon color='info' onClick={this.handleShowPrevMap}>
									<ArrowBack />
								</Button>
								<Button size='lg' simple justIcon color='info' onClick={this.handleShowNextMap}>
									<ArrowForward />
								</Button>
								</div>
							</GridItem>
						</GridContainer>
						</>}
						</>
					)}
				</ErrorComponent>
			);
		} catch (error) {
			console.log('ERROR_DEBUG', error);
			return <ErrorText message='Corrupted map data..' />;
		}
	}
}

function MapEvents({ onMapClick, onMapBlur }) {
  useMapEvent('click', () => {
    onMapClick();
  });

  useMapEvent('blur', () => {
    onMapBlur();
  });

  return null;
}

const mapStateToProps = (state) => {
	return {
		secondMapDataBlob: state.dashboard.systemSecondMapDataBlob,
		thirdMapDataBlob: state.dashboard.systemThirdMapDataBlob,
	};
};

export default connect(mapStateToProps, {})(HeatMap);